import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { CatalogueItem } from '../../utils/types';
import {
  getCatalogueItem,
  updateItemDetails,
} from '../../hooks/catalogueItems';
import {
  deleteCatalogueItemImage,
  uploadCatalogueItemImage,
} from '../../hooks/catalogueItemImages';
import ConfirmModal from '../../components/ConfirmationModal';
import CatalogueMediaManager from './components/CatalogueMediaManager';
import {
  CatalogueField,
  CatalogueHero,
  CatalogueListSkeleton,
  CatalogueSectionCard,
  CatalogueShell,
  PrimaryCatalogueButton,
  formatCurrency,
} from './components/catalogueUi';

type EditableItem = Pick<
  CatalogueItem,
  'name' | 'description' | 'price' | 'is_active'
> & { id: string };

const EditCatalogueItemPage: React.FC = () => {
  const { orgId, itemId, catalogueId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [product, setProduct] = useState<CatalogueItem | null>(null);
  const [draft, setDraft] = useState<EditableItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  const fetchProduct = async () => {
    if (!orgId || !itemId || !catalogueId) {
      return;
    }

    setLoading(true);
    const result = await getCatalogueItem(orgId, catalogueId, itemId);
    setLoading(false);
    setLoadAttempted(true);

    if (!result.success || !result.data) {
      toast({
        title: 'Unable to load item',
        description: 'We could not retrieve the latest product details.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setProduct(result.data);
    setDraft({
      id: result.data.id,
      name: result.data.name,
      description: result.data.description,
      price: result.data.price,
      is_active: result.data.is_active,
    });
  };

  useEffect(() => {
    fetchProduct();
  }, [orgId, itemId, catalogueId]);

  const changedData = useMemo(() => {
    if (!product || !draft) {
      return {};
    }

    const changes: Partial<CatalogueItem> = {};

    if (draft.name !== product.name) {
      changes.name = draft.name;
    }
    if (draft.description !== product.description) {
      changes.description = draft.description;
    }
    if (Number(draft.price) !== Number(product.price)) {
      changes.price = Number(draft.price);
    }
    if (draft.is_active !== product.is_active) {
      changes.is_active = draft.is_active;
    }

    return changes;
  }, [draft, product]);

  const hasChanges = Object.keys(changedData).length > 0;

  const handleSave = async () => {
    if (!orgId || !catalogueId || !itemId || !draft) {
      return;
    }

    if (!hasChanges) {
      toast({
        title: 'No changes to save',
        description: 'Update one or more fields before saving.',
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    const response = await updateItemDetails(orgId, itemId, catalogueId, changedData);
    setSaving(false);

    if (!response.success) {
      toast({
        title: 'Save failed',
        description: 'We could not update the catalogue item. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Item updated',
      description: `${draft?.name || 'Item'} has been saved successfully.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    await fetchProduct();
  };

  const handleUpload = async (file: File) => {
    if (!orgId || !catalogueId || !itemId) {
      return;
    }

    setUploading(true);
    const response = await uploadCatalogueItemImage(orgId, itemId, catalogueId, file);
    setUploading(false);

    if (!response.success) {
      toast({
        title: 'Image upload failed',
        description: 'Please try again with another file.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Image added',
      description: 'The media library has been updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    fetchProduct();
  };

  const requestDeleteImage = (imageId: string) => {
    setSelectedImageId(imageId);
    onOpen();
  };

  const handleDeleteImage = async () => {
    if (!orgId || !catalogueId || !itemId || !selectedImageId) {
      return;
    }

    const response = await deleteCatalogueItemImage(
      orgId,
      catalogueId,
      itemId,
      selectedImageId,
    );

    if (!response.success) {
      toast({
        title: 'Image could not be deleted',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Image deleted',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
    onClose();
    setSelectedImageId(null);
    fetchProduct();
  };

  if (loading) {
    return (
      <CatalogueShell compact>
        <CatalogueListSkeleton />
      </CatalogueShell>
    );
  }

  if (loadAttempted && (!product || !draft)) {
    return (
      <CatalogueShell compact>
        <CatalogueSectionCard
          title="Item unavailable"
          description="The catalogue item could not be loaded. Go back to the catalogue and try again."
        >
          <Button borderRadius="16px" onClick={() => navigate(`/organization/${orgId}/catalogue`)}>
            Back to catalogue
          </Button>
        </CatalogueSectionCard>
      </CatalogueShell>
    );
  }

  if (!product || !draft) {
    return null;
  }

  const currentProduct = product;
  const currentDraft = draft;

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="Editor"
        title={currentDraft.name || 'Edit catalogue item'}
        description="Update the item details, change the image, and keep your catalogue up to date."
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <CatalogueSectionCard
            title="Basics"
            description="Update the main product details."
          >
            <Stack spacing={5}>
              <FormControl isRequired>
                <FormLabel>Item name</FormLabel>
                <Input
                  value={currentDraft.name}
                  onChange={(event) =>
                    setDraft((current) =>
                      current ? { ...current, name: event.target.value } : current,
                    )
                  }
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={currentDraft.description}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? { ...current, description: event.target.value }
                        : current,
                    )
                  }
                  minH="180px"
                  borderRadius="16px"
                  bg="gray.50"
                />
                <FormHelperText>
                  Keep the description sharp and operational rather than verbose.
                </FormHelperText>
              </FormControl>
            </Stack>
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Commercial controls"
            description="Update the price and choose whether the item stays active."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl>
                <FormLabel>Price (USD)</FormLabel>
                <Input
                  type="number"
                  step="0.01"
                  value={currentDraft.price}
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? { ...current, price: Number(event.target.value) || 0 }
                        : current,
                    )
                  }
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={1}>Item status</FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    Toggle off to keep the item in the system without treating it as active.
                  </Text>
                </Box>
                <Switch
                  isChecked={currentDraft.is_active}
                  colorScheme="green"
                  onChange={(event) =>
                    setDraft((current) =>
                      current
                        ? { ...current, is_active: event.target.checked }
                        : current,
                    )
                  }
                />
              </FormControl>
            </SimpleGrid>
          </CatalogueSectionCard>

          <CatalogueMediaManager
            images={currentProduct.images || []}
            onUpload={handleUpload}
            onDelete={requestDeleteImage}
            uploading={uploading}
            title="Media library"
            description="Add or remove images for this item."
          />
        </Stack>

        <Stack spacing={6}>
          <CatalogueSectionCard
            title="Save state"
            description="Save your changes or go back to the item page."
          >
            <VStack align="stretch" spacing={3}>
              <PrimaryCatalogueButton
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving"
              >
                Save changes
              </PrimaryCatalogueButton>
              <Button
                borderRadius="16px"
                variant="outline"
                onClick={() =>
                  navigate(
                    `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${itemId}`,
                  )
                }
              >
                View item details
              </Button>
              <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
                Back
              </Button>
            </VStack>
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Current state"
            description="A quick summary of the current item details."
          >
            <Stack spacing={4}>
              <CatalogueField
                label="Save status"
                value={hasChanges ? 'Unsaved changes' : 'All changes saved'}
              />
              <CatalogueField
                label="Price"
                value={formatCurrency(Number(currentDraft.price))}
              />
              <CatalogueField
                label="Media"
                value={
                  currentProduct.images?.length
                    ? `${currentProduct.images.length} image${currentProduct.images.length > 1 ? 's' : ''}`
                    : 'No images yet'
                }
              />
              <CatalogueField
                label="Visibility"
                value={currentDraft.is_active ? 'Active' : 'Inactive'}
              />
            </Stack>
          </CatalogueSectionCard>
        </Stack>
      </SimpleGrid>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDeleteImage}
        isLoading={false}
        AreYouSureQuestion="Delete this image?"
        AreYouSureTextExplanation="This removes the selected image from the item and cannot be undone."
        confirmButtonText="Delete image"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </CatalogueShell>
  );
};

export default EditCatalogueItemPage;
